import React, { useEffect, useRef, useState } from 'react';
import { X, Delete } from 'lucide-react';

/**
 * NumericKeypad – kalkulator-style tastatur for rask poeng-inntasting.
 * Åpnes som modal fra bunn (mobil) eller ved siden av input (desktop).
 * Kun heltall (0-9). Bruker kan slette siste eller alle.
 */
const NumericKeypad = ({ open, onClose, onSubmit, initialValue = '', title = 'Poengsum', min = 0, max = 100 }) => {
  const [value, setValue] = useState(String(initialValue || ''));
  const containerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setValue(String(initialValue || ''));
    }
  }, [open, initialValue]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        handleDigit(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      } else if (e.key === 'Enter') {
        handleSubmit();
      } else if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, value]);

  const handleDigit = (d) => {
    const next = (value + d).replace(/^0+(?=\d)/, '');
    const num = parseInt(next, 10);
    if (!isNaN(num) && num > max) return; // ikke tillat over max
    setValue(next);
    try { if (navigator.vibrate) navigator.vibrate(6); } catch {}
  };
  const handleBackspace = () => {
    setValue((v) => v.slice(0, -1));
    try { if (navigator.vibrate) navigator.vibrate(4); } catch {}
  };
  const handleClear = () => setValue('');
  const handleSubmit = () => {
    const num = parseInt(value, 10);
    if (isNaN(num)) return onClose?.();
    if (num < min || num > max) return;
    onSubmit(num);
    onClose?.();
  };

  if (!open) return null;

  const keys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
  ];

  return (
    <div className="fixed inset-0 z-[120] flex items-end md:items-center justify-center bg-black/60" onClick={onClose} data-testid="numeric-keypad-overlay">
      <div
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 border border-gray-700 rounded-t-2xl md:rounded-2xl w-full md:max-w-sm p-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-2xl"
        data-testid="numeric-keypad"
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-white font-bold">{title}</p>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        {/* Display */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 mb-4 text-right">
          <div className="text-white font-mono text-4xl min-h-[3rem]" data-testid="keypad-display">
            {value || <span className="text-gray-600">—</span>}
          </div>
          <p className="text-xs text-gray-500 mt-1">Gyldig: {min}–{max} (hele poeng)</p>
        </div>

        {/* Number keys */}
        <div className="grid grid-cols-3 gap-2">
          {keys.flat().map((k) => (
            <button
              key={k}
              onClick={() => handleDigit(k)}
              className="h-14 bg-gray-800 hover:bg-gray-700 active:bg-yellow-500 active:text-gray-900 text-white text-2xl font-semibold rounded-lg transition-colors touch-manipulation"
              data-testid={`keypad-key-${k}`}
            >
              {k}
            </button>
          ))}
          <button
            onClick={handleClear}
            className="h-14 bg-gray-800 hover:bg-red-600/40 text-red-300 text-sm font-semibold rounded-lg transition-colors touch-manipulation"
            data-testid="keypad-key-clear"
          >
            C
          </button>
          <button
            onClick={() => handleDigit('0')}
            className="h-14 bg-gray-800 hover:bg-gray-700 active:bg-yellow-500 active:text-gray-900 text-white text-2xl font-semibold rounded-lg transition-colors touch-manipulation"
            data-testid="keypad-key-0"
          >
            0
          </button>
          <button
            onClick={handleBackspace}
            className="h-14 bg-gray-800 hover:bg-gray-700 text-yellow-300 rounded-lg flex items-center justify-center transition-colors touch-manipulation"
            data-testid="keypad-key-backspace"
          >
            <Delete className="w-6 h-6" />
          </button>
        </div>

        <button
          onClick={handleSubmit}
          className="w-full mt-4 h-14 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-lg rounded-lg transition-colors touch-manipulation"
          data-testid="keypad-submit"
        >
          Bekreft
        </button>
      </div>
    </div>
  );
};

export default NumericKeypad;
